import { jsPDF } from 'jspdf';
import KfLogo from '../../../assets/kf-logo-rosasymbol-svart-cmyk.png'
import FellixSemiBoldFontUrl from '../../../fonts/fellix/Fellix-SemiBold.ttf';
import FellixRegularFontUrl from '../../../fonts/fellix/Fellix-Regular.ttf';
import FellixBoldFontUrl from '../../../fonts/fellix/Fellix-Bold.ttf';

// ************************************************************************
// Farger
// ************************************************************************

// doc.setFillColor(85, 31, 47); // Rød header i KP, #FFFFFF
// doc.setTextColor(246, 234, 190); // Gul tekst i header, #F6EABE
// doc.setFillColor(200, 173, 127); // Gul knapp i KP, #c8ad7f
// doc.setFillColor(4, 79, 103); // Blå knapp i KP, #044F67
// doc.setTextColor(0, 0, 0); // Svart tekst osv, #000000
// doc.setTextColor(255, 255, 255); // Hvit, #FFFFFF

const næringPdfV2 = async (data, user, date, kreditor, orgNumber, localeMessages) => {
  
  // ************************************************************************
  // Hjelpere
  // ************************************************************************

  async function loadFontAsBase64(url) {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const formatDate = (currentDate) => {
    if (!currentDate) return '';
    const day = currentDate.getDate().toString().padStart(2, "0");
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const year = currentDate.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatNumberWithSpaces = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const checkNumberValue = (num) => {
    if (num === '' || num === undefined || num === null || num === 0 || num === '0') {
      return '0';
    }
    return num.toString();
  }

  const checkString = (value) => {
    if (value === undefined || value === null || value === '') {
      return '-'
    }
    return value.toString();
  }

  const setDynamicFontSize = (wordWidth) => {
    if (!wordWidth) return 0;
  
    switch (true) {
      case wordWidth < 70:
        return 20;
      case wordWidth < 80:
        return 16;
      case wordWidth < 90:
        return 14;
      case wordWidth < 100:
        return 12;
      default:
        return 20;
    }
  };

  // ************************************************************************
  // Lag dokument
  // ************************************************************************
  
  // Lag nytt jsPDF dokument
  const doc = new jsPDF();

  // Funksjon for å sjekke om ny side må legges til
  const addNewPageIfNeeded = (doc, currentY, marginBottom = 10) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (currentY > pageHeight - marginBottom) {
      doc.addPage();
      return 20; // ny y-koordinat med litt margin
    }
    return currentY;
  };

  const getFileName = () => {
    const nameValue = data.foretaksnavn && data.foretaksnavn.trim() !== '' 
        ? data.foretaksnavn 
        : '';
    
    return `Kredittvurdering ${nameValue}.pdf`;
  };


  // ************************************************************************
  // Fonter
  // ************************************************************************

  // Laster og registrerer alle fonter
  const boldBase64 = await loadFontAsBase64(FellixBoldFontUrl);
  doc.addFileToVFS('Fellix-Bold.ttf', boldBase64);
  doc.addFont('Fellix-Bold.ttf', 'Fellix', 'bold');

  const semiboldBase64 = await loadFontAsBase64(FellixSemiBoldFontUrl);
  doc.addFileToVFS('Fellix-SemiBold.ttf', semiboldBase64);
  doc.addFont('Fellix-SemiBold.ttf', 'Fellix', 'semiBold');

  const regularBase64 = await loadFontAsBase64(FellixRegularFontUrl);
  doc.addFileToVFS('Fellix-Regular.ttf', regularBase64);
  doc.addFont('Fellix-Regular.ttf', 'Fellix', 'normal');


  // ************************************************************************
  // Header
  // ************************************************************************

  // Bestemmer header høyde og bredden på pdf. (units, e.g., mm)
  const headerHeight = 30;
  const pageWidth = doc.internal.pageSize.getWidth();

  // Farger og lager header som Logo og bruker-tekst skal inn i
  doc.setFillColor(255, 255, 255); // Rød header i KP, #FFFFFF
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(10);

  // Header-teksten skal være på venstre side
  let currentY = 0; // Hjelper for å kunne holde styr på hvor vi er og om vi må legge til ny side
  const leftMargin = 10; // Margin venstre som tekst bruker
  const kredittvurderingY = 8; // y-koordinator for kredittvurdering linjen
  const kreditorY = 18; // y-koordinater for kreditor linjen
  const dateY = 13; // y-koordinater for dato linjen
  const userY = 23; // y-koordinater for bruker linjen
  const marginBottom = 10; // Brukes når vi sjekker om vi må legge til ny side
  const verticalSpacing = 12; // Vertikal spacing mellom tittel og verdi
  const pad = 5; // Padding for hver kolonne


  // Sub header verdier
  const headerLeftMargin = 8; // Margin for sub headers
  const headerRightMargin = 8; // Margin for sub headers
  const subHeaderHeight = 12; // Høyde for sub headers
  const subHeaderWidth = pageWidth - headerLeftMargin - headerRightMargin; // Bredde for sub headers
  const cornerRadius = 3; // Avrundede hjørner for sub headers
  const subHeaderTekstX = headerLeftMargin + 3; // Teksten i sub headers skal ha en liten margin til venstre
  let subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Plasserer teksten vertikalt i sub headers, denne oppdateres underveis

  // Type kredittvurdering
  doc.setFont("Fellix", "normal");
  doc.text("Type: ", leftMargin, kredittvurderingY);
  const typeLabelWidth = doc.getTextWidth("Type: ");
  doc.setFont("Fellix", "semiBold");
  doc.text("Kredittvurdering", leftMargin + typeLabelWidth, kredittvurderingY);

  // Kreditor som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Kreditor: ", leftMargin, kreditorY);
  const kreditorLabelWidth = doc.getTextWidth("Kreditor: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(kreditor), leftMargin + kreditorLabelWidth, kreditorY);

  // Dato utført
  doc.setFont("Fellix", "normal");
  doc.text("Dato utført: ", leftMargin, dateY);
  const dateLabelWidth = doc.getTextWidth("Dato utført: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(formatDate(date), leftMargin + dateLabelWidth, dateY);

  // Bruker som har utført
  doc.setFont("Fellix", "normal");
  doc.text("Utført av: ", leftMargin, userY);
  const userLabelWidth = doc.getTextWidth("Utført av: ");
  doc.setFont("Fellix", "semiBold");
  doc.text(checkString(user), leftMargin + userLabelWidth, userY);

  // Logo
  const logoWidth = 60;  // Logo sin bredde i mm
  const logoHeight = 15; // Logo sin høyde i mm
  const logoX = pageWidth - logoWidth - 10; // Margin fra høyre kant
  const logoY = 5; // Sentrer i midten av header
  doc.addImage(KfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);


  // ************************************************************************
  // Om debitor/den som er vurdert
  // ************************************************************************

  // Foretaksnavn
  currentY += 45; // y-koordinater for navn linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setFont("Fellix", "bold");
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(22);
  doc.text(checkString(data.foretaksnavn), leftMargin, currentY);

  currentY += 10; // y-koordinator for personnr og type tag
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Personnr og type tag skal være rett under navn
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(14);
  doc.text(orgNumber, leftMargin, currentY);

  const orgNumberWidth = doc.getTextWidth(orgNumber);
  // Kalkulerer bredden til tekst for sentrering i rektangel
  const typeWidth = doc.getTextWidth("Næring");
  const typeRectangleWidth = typeWidth;
  const typeRectangleHeight = 6;
  const maxTextWidth = 50;

  // Rektangel sin x-posisjon skal være rett etter personnr + litt margin
  const rectangleX = leftMargin + orgNumberWidth + 4;

  // Setter svart border og line width for rektangel
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);

  // Lager rektangel for type med 1mm border radius
  doc.roundedRect(rectangleX, currentY - 5, typeRectangleWidth, typeRectangleHeight, 1, 1, 'S'); 
  
  const typeX = rectangleX + (typeRectangleWidth - typeWidth) / 2;

  // Add the text inside the rectangle
  doc.setFontSize(10);
  doc.text("Næring", typeX + 2.5, currentY - 0.7);

  currentY += 15; // y-koordinater for generell info linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  const generalInfoRowSpacing = 7; // Vertikal spacing mellom tittel og verdi
  const columnSpacing = 50; // Horisontal spacing mellom kolonnene

  // Kolonne 1: Daglig leder
  const dagLedTextLines = doc.splitTextToSize(data.dagligLederNavn, maxTextWidth); // Splitter teksten basert på definert maksbredde
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['dagliglederLabel'], leftMargin, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(dagLedTextLines, leftMargin, currentY + generalInfoRowSpacing);

  // Kolonne 2: Adresse
  const col2X = leftMargin + columnSpacing;
  const adresseTextLines = doc.splitTextToSize(data.forretningsadresseAdresse, maxTextWidth); // Splitter teksten basert på definert maksbredde
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['addressLabel'], col2X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(adresseTextLines, col2X, currentY + generalInfoRowSpacing);

  // Kolonne 3: Kommune
  const col3X = leftMargin + columnSpacing * 2;
  const kommuneTextLines = doc.splitTextToSize(data.forretningsadresseKommune, maxTextWidth);
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['municipalityLabel'], col3X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(kommuneTextLines, col3X, currentY + generalInfoRowSpacing);

  // Kolonne 4: Postnummer
  const col4X = leftMargin + columnSpacing * 3.1;
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['postnumberLabel'], col4X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(checkString(data.forretningsadressePostnummer), col4X, currentY + generalInfoRowSpacing);


  // ************************************************************************
  // Oversikt
  // ************************************************************************
 
  // Høyde for Oversikt header
  currentY += 25; // Hvor oversikt header skal starte vertikalt
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Farger og lager sub header
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Oversikt", subHeaderTekstX, subHeaderTekstY); 

  currentY += 25; // y-koordinater for score linjen
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setTextColor(0, 0, 0);
  const scoreSpacing = 10;

  // Kalkulerer bredde for alle kolonnene
  // Kolonne 1: Rating
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col1LabelWidth = doc.getTextWidth(localeMessages['gradeLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col1ValueWidth = doc.getTextWidth(String(data.scoreKarakter)) + pad;
  const col1Width = Math.max(col1LabelWidth, col1ValueWidth);

  // Kolonne 2: Score
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col2LabelWidth = doc.getTextWidth(localeMessages['scoreLabel']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const col2ValueWidth = doc.getTextWidth(String(data.score)) + pad;
  const col2Width = Math.max(col2LabelWidth, col2ValueWidth);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const col3LabelWidth = doc.getTextWidth(localeMessages['ratingLabel']) + pad;
  doc.setFont("Fellix", "normal");
  doc.setFontSize(20);
  const col3ValueWidth = doc.getTextWidth(data.scoreForklaring) + pad;
  const col3Width = Math.max(col3LabelWidth, col3ValueWidth);
  const reduserVurderingWidth = col3ValueWidth > 60;

  // Kalkulerer x-posisjoner basert på bredde
  const scoreCol1X = leftMargin;
  const scoreCol2X = scoreCol1X + col1Width + scoreSpacing;
  const scoreCol3X = scoreCol2X + col2Width + scoreSpacing;
  const scoreCol4X = reduserVurderingWidth ? (scoreCol3X - 13) + col3Width + scoreSpacing : scoreCol3X + col3Width + scoreSpacing;

  // Kolonne 1: Rating
  doc.setTextColor(0, 0, 0);
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['gradeLabel'], scoreCol1X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkString(data.scoreKarakter), scoreCol1X, currentY + verticalSpacing);

  // Kolonne 2: Score
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['scoreLabel'], scoreCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkNumberValue(data.score), scoreCol2X, currentY + verticalSpacing);

  // Kolonne 3: Vurdering
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['ratingLabel'], scoreCol3X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(setDynamicFontSize(col3ValueWidth)); // Justerer fontsize basert på vurdering sin lengde
  doc.text(checkString(data.scoreForklaring), scoreCol3X, currentY + verticalSpacing);

  // Kolonne 4: Limit
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['recommended_limitLabel'], scoreCol4X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.kredittgrense), scoreCol4X, currentY + verticalSpacing);  



  // ************************************************************************
  // Økonomi
  // ************************************************************************

  // Høyde for Økonomi sub header
  currentY += 35;
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Blå header
  doc.setFillColor(255, 255, 255); // Blå knapp i KP, #044F67
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Hvit, #FFFFFF
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Økonomi", subHeaderTekstX, subHeaderTekstY);

  currentY += 25; // Vertikal posisjon for økonomi-verdiene
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setTextColor(0, 0, 0);
  const økonomiSpacing = 20;

  // Kalkulerer bredde for alle kolonnene
  // Kolonne 1: Driftsinntekter
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const økonomiCol1LabelWidth = doc.getTextWidth(localeMessages['revenues']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const økonomiCol1ValueWidth = doc.getTextWidth(String(data.driftsinntekter)) + pad;
  const økonomiCol1Width = Math.max(økonomiCol1LabelWidth, økonomiCol1ValueWidth);

  // Kolonne 2: Driftskostnader
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const økonomiCol2LabelWidth = doc.getTextWidth(localeMessages['operating_costs']) + pad;
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  const økonomiCol2ValueWidth = doc.getTextWidth(String(data.driftskostnader)) + pad;
  const økonomiCol2Width = Math.max(økonomiCol2LabelWidth, økonomiCol2ValueWidth);

  // Kolonne 3: Driftsresultat
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  const økonomiCol3LabelWidth = doc.getTextWidth(localeMessages['earnings']) + pad;
  doc.setFont("Fellix", "normal");
  doc.setFontSize(20);
  const økonomiCol3ValueWidth = doc.getTextWidth(String(data.driftsresultat)) + pad;
  const økonomiCol3Width = Math.max(økonomiCol3LabelWidth, økonomiCol3ValueWidth);

  // Kalkulerer x posisjoner basert på bredde
  const økonomiCol1X = leftMargin;
  const økonomiCol2X = økonomiCol1X + økonomiCol1Width + økonomiSpacing;
  const økonomiCol3X = økonomiCol2X + økonomiCol2Width + økonomiSpacing;
  const økonomiCol4X = (økonomiCol3X - 12) + økonomiCol3Width + økonomiSpacing;

  // Kolonne 1: Driftsinntekter
  doc.setTextColor(0, 0, 0);
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['revenues'], økonomiCol1X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.driftsinntekter), økonomiCol1X, currentY + verticalSpacing);

  // Kolonne 2: Formue
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['operating_costs'], økonomiCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.driftskostnader), økonomiCol2X, currentY + verticalSpacing);

  // Kolonne 3: Skatt
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['earnings'], økonomiCol3X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.driftsresultat), økonomiCol3X, currentY + verticalSpacing);

  // Kolonne 4: Ligningsår
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['financial_year'], økonomiCol4X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(checkString(data.regnskapsaar), økonomiCol4X, currentY + verticalSpacing);

  // Ny rad med økonomi-informasjon

  currentY += 25; // Vertikal posisjon for ny rad
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setTextColor(0, 0, 0);

  // Kolonne 1: Daglig leder
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['total_equity'], leftMargin, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.sumEgenkapital), leftMargin, currentY + verticalSpacing);

  // Kolonne 2: Styreleder
//   const økonomiCol2X = leftMargin + økonomiSpacing;
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['total_assets'], økonomiCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.sumEiendeler), økonomiCol2X, currentY + verticalSpacing);

  // Kolonne 3: Styremedlem
//   const økonomiCol3X = leftMargin + økonomiSpacing * 2;
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['total_liabilities'], økonomiCol3X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.sumGjeld), økonomiCol3X, currentY + verticalSpacing);



  // ************************************************************************
  // Anmerkninger
  // ************************************************************************
 
  // Høyde for Anmerkninger sub header
  currentY += 35; // Ny side om >= 300
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Farger og lager header
  doc.setFillColor(255, 255, 255); // Gul knapp i KP, #c8ad7f
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Svart, #000000
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Anmerkninger", subHeaderTekstX, subHeaderTekstY);

  currentY += 25; // Vertikal posisjon for anmerkninger-verdiene
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setTextColor(0, 0, 0);
  const anmerkningerSpacing = 50; // Spacing mellom hver kolonne

  // Kolonne 1: Antall anmerkninger
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['number_remarksLabel'], leftMargin, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.betalingsanmerkningerAntall), leftMargin, currentY + verticalSpacing);

  // Kolonne 2: Beløp anmerkninger
  const anmerkningerCol2X = leftMargin + anmerkningerSpacing;
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['amount_remarksLabel'], anmerkningerCol2X, currentY);
  doc.setFont("Fellix", "bold");
  doc.setFontSize(20);
  doc.text(formatNumberWithSpaces(data.betalingsanmerkningerBelop), anmerkningerCol2X, currentY + verticalSpacing);


  // ************************************************************************
  // Foretaksinformasjon
  // ************************************************************************

  // Delvis den samme informasjonen som er øverst under foreteksnavnet i PDFen
  // Gjenbruker derfor noen av verdiene, som col sin bredde osv.

  // Høyde for eierskap sub header   
  currentY += 35;
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  // Farger og lager header
  doc.setFillColor(255, 255, 255); // Rød header som i KP, #FFFFFF
  doc.roundedRect(headerLeftMargin, currentY, subHeaderWidth, subHeaderHeight, cornerRadius, cornerRadius, 'F');

  // Setter fontfarge og size for tekst i header
  doc.setTextColor(0, 0, 0); // Gul, #F6EABE
  doc.setFontSize(16);
  doc.setFont("Fellix", "bold");
  subHeaderTekstY = currentY + subHeaderHeight / 2 + 2; // Oppdaterer sub header tekst sin y-posisjon
  doc.text("Foretaksinformasjon", subHeaderTekstX, subHeaderTekstY);

  currentY += 25; // Vertikal posisjon for eierskap-verdiene
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  doc.setTextColor(0, 0, 0);
 
  // Kolonne 1: Daglig leder
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['dagliglederLabel'], leftMargin, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(dagLedTextLines, leftMargin, currentY + generalInfoRowSpacing);

  // Kolonne 2: Adresse
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['addressLabel'], col2X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(adresseTextLines, col2X, currentY + generalInfoRowSpacing);

  // Kolonne 3: Kommune
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['municipalityLabel'], col3X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(kommuneTextLines, col3X, currentY + generalInfoRowSpacing);

  // Kolonne 4: Postnummer
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['postnumberLabel'], col4X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(checkString(data.forretningsadressePostnummer), col4X, currentY + generalInfoRowSpacing);

  // Ny rad med foretaksinformasjon

  currentY += 25; // Hvor neste rad skal starte vertikalt
  currentY = addNewPageIfNeeded(doc, currentY, marginBottom);

  //Kolonne 1: Bransje
  const bransjeMaxWidth = 50; // Setter en maks bredde da bransje kan ha mye tekst 
  // splitTextToSize splitter basert på spaces i teksten, med mindre ordet er lengre enn maxWidth
  const bransjeTextLines = doc.splitTextToSize(data.naeringskode, bransjeMaxWidth); // Splitter teksten basert på definert maksbredde
  doc.setFont("Fellix", "semiBold");
  doc.setFontSize(12);
  doc.text(localeMessages['industryLabel'], leftMargin, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(bransjeTextLines, leftMargin, currentY + generalInfoRowSpacing);

  // Kolonne 2: Organisasjonsform
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['orgformLabel'], col2X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(checkString(data.organisasjonsform), col2X, currentY + generalInfoRowSpacing);

  // Kolonne 3: Stiftelsesdato
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['foundingdateLabel'], col3X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(checkString(data.stiftelsesdato), col3X, currentY + generalInfoRowSpacing);

  // Kolonne 3: Ansatte
  doc.setFont("Fellix", "semiBold");
  doc.text(localeMessages['number_employees'], col4X, currentY);
  doc.setFont("Fellix", "normal");
  doc.text(checkNumberValue(data.antallAnsatte), col4X, currentY + generalInfoRowSpacing);



  // // Predefined fields with keys and localized titles
  // const predefinedFields = [
  //   { key: 'navn', label: localeMessages['nameLabel'] || 'Navn' },
  //   { key: 'bostedsadresseAdresse', label: localeMessages['addressLabel'] || 'Adresse' },
  //   { key: 'bostedsadressePostnummer', label: localeMessages['postnumberLabel'] || 'Postnummer' },
  //   { key: 'bostedsadresseKommune', label: localeMessages['municipalityLabel'] || 'Kommune' },
  //   // Add more fields as needed
  // ];

  // // Loop over each predefined field. For each field present in data, print title and value.
  // predefinedFields.forEach(field => {
  //   const value = data[field.key];
  //   // Only show if there is a non-empty value
  //   if (value !== undefined && value !== null && value !== '') {
  //     // Print the title in bold
      
  //     doc.text(field.label, currentX, currentY);
  //     // Print the value below the title in normal font
  //     doc.setFont('Fellix', 'semiBold');
  //     doc.text(String(value), currentX, currentY + 7);

  //     colCount++;
  //     currentX += columnWidth;

  //     // When three pairs have been printed in this row, move to the next row.
  //     if (colCount === pairsPerRow) {
  //       colCount = 0;
  //       currentX = marginLeft;
  //       currentY += rowHeight;
  //     }
  //   }
  // });

  return doc.output('blob')

};

export default næringPdfV2;
